import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { onboardingApi } from '../api/onboardingApi';
import { useToast } from '../../../shared/components/toast/useToast';
import type { StorefrontStatus, GoLiveRequest } from '../types';

const errMsg = (err: unknown, fallback: string) => {
  const msg = (err as { message?: unknown })?.message;
  if (Array.isArray(msg)) return (msg as string[])[0] || fallback;
  return (typeof msg === 'string' ? msg : '') || fallback;
};

const statusKey = (slug: string) => ['storefront-onboarding', slug, 'status'] as const;

/** Per-merchant storefront standup status. Polls so the board reflects in-flight work. */
export const useStorefrontStatus = (slug: string) =>
  useQuery<StorefrontStatus>({
    queryKey: statusKey(slug),
    queryFn: () => onboardingApi.getStatus(slug),
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
    enabled: !!slug,
  });

/**
 * Dispatch infra provisioning (repo + Cloudflare site) for a merchant slug.
 * Invalidates that slug's status so the board re-reads after dispatch.
 */
export const useProvisionInfra = (slug: string) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: () => onboardingApi.provisionInfra(slug),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: statusKey(slug) });
      toast.success('Infra provisioning dispatched', res.message);
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to dispatch infra provisioning')),
  });
};

/**
 * Flip a merchant's storefront from DEMO_MODE to live. The readiness gate now
 * derives `keyIssued` authoritatively (osprey-api probes the api-key endpoint), so
 * a ready tenant goes live without `{ force: true }`; force remains a superadmin
 * override for edge cases. Invalidates that slug's status so the deploy leg re-reads.
 */
export const useGoLive = (slug: string) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (body: GoLiveRequest = {}) => onboardingApi.goLive(slug, body),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: statusKey(slug) });
      toast.success('Go-live dispatched', res.message);
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to dispatch go-live')),
  });
};
